import React from 'react';
import './App.css';
import cartoonCat from './assets/images/CartoonCat.jpeg';
import winnerImage from './assets/images/Winner.png';
import carttonImage from './assets/images/Cartton.jpeg';
import languageSdksImage from './assets/images/Language_Sdks.jpeg';
import pricingImage from './assets/images/PricingImage.jpeg';
import tldrImage from './assets/images/Tldr.png';

function App() {
  return (
    <div className="app">
      <div className="content-wrapper">
        <div className="text-section">
          <p className="intro-text">We're here to help video engineers build successful products</p>
          <p className="main-text">Literally every piece of video infrastructure a team needs.</p>
          <p className="description-text">
            This includes tools for adding video, going live, streaming at scale, protecting content, and understanding how video behaves once users depend on it.
          </p>
          <p className="platform-text">This is a single platform for people who ship video in production.</p>
        </div>
        <div className="illustration-section">
          <img src={cartoonCat} alt="Cartoon Cat" className="illustration-image" />
        </div>    
      </div>

      <div className="divider-line"></div>
       <div className="section-2">
        <h2 className="section-2-title">Why we're different</h2>
        <p className="section-2-text">
          We built this platform because we were tired of running video across five different systems one for uploads, another for live, another for playback, another for analytics, and a spreadsheet to keep track of costs.
        </p>
        <p className="section-2-text">
          That fragmentation has somehow become normal in video. We don't think it should be.
        </p>
        <p className="section-2-text">
          So we built the platform we always wanted to use. We decided to put the whole workflow in one place instead.
        </p>
        <ul className="section-2-list">
          <li>Adding video.</li>
          <li>Going live.</li>
          <li>Processing.</li>
          <li>Streaming.</li>
          <li>Measuring performance.</li>
        </ul>
        <p className="section-2-text">
          When these pieces live together, teams spend less time wiring systems and more time fixing real problems.
        </p>
      </div> 
      <div className="divider-line"></div>

      <div className="section-3">
        <div className="section-3-content">
          <div className="section-3-text">
            <p className="section-3-line1">There are other video API companies,</p>
            <p className="section-3-line2">but they not like us</p>
          </div>
          <div className="section-3-illustration">
            <img src={winnerImage} alt="Winner" className="winner-image" />
          </div>
        </div>

        <div className="comparison-table-container">
          <table className="comparison-table">
            <thead>
              <tr>
                <th>Video duration</th>
                <th>Max resolution, File size, Codec</th>
                <th>FastPix</th>
                <th>Mux</th>
                <th>Vimeo/brightcove</th>
                <th>Kaltura</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1 min 20 sec</td>
                <td>1080p, 101 MB, H.264</td>
                <td>7 sec</td>
                <td>2 sec</td>
                <td>55 sec</td>
                <td>39 sec</td>
              </tr>
              <tr>
                <td>13 mins</td>
                <td>1080p, 151 MB, H.264</td>
                <td>20 sec</td>
                <td>8 sec</td>
                <td> 1 min 2 sec</td>
                <td>37 sec</td>
              </tr>
              <tr>
                <td>30 mins</td>
                <td>1080p, 401 MB, H.264</td>
                <td>34 sec</td>
                <td>18 sec</td>
                <td>1 min 27 sec</td>
                <td>1 min 27 sec</td>
              </tr>
              <tr>
                <td>47 mins</td>
                <td>1080p, 661 MB, H.264</td>
                <td>21 sec</td>
                <td>20 sec</td>
                <td>1 min 6 sec</td>
                <td>2 min 32 sec</td>
              </tr>
              <tr>
                <td>1 hr 23 mins</td>
                <td>1080p, 365 MB, H.264</td>
                <td>17 sec</td>
                <td>24 sec</td>
                <td>1 min 32 sec</td>
                <td>1 min 57 sec</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="divider-line"></div>

      <div className="section-4">
        <div className="section-4-content">
          <div className="section-4-text">
            <h2 className="section-4-title">It fits into your stack</h2>
            <p className="section-4-description">
              You shouldn't have to bend your architecture around video. The SDKs plug into the tools you already use, so video workflows live inside your product instead of beside it.
            </p>
          </div>
          <div className="section-4-illustration">
            <img src={carttonImage} alt="Beach Fox" className="beach-fox-image" />
          </div>
        </div>

        <div className="language-sdks-section">
          <h3 className="language-sdks-title">Language SDKs</h3>
          <div className="language-sdks-container">
            <img src={languageSdksImage} alt="Language SDKs" className="language-sdks-image" />
          </div>
        </div>
      </div>
      <div className="divider-line"></div>

      <div className="section-5">
        <p className="section-5-tagline">We just do the right thing.</p>
        <h2 className="section-5-title">Everything required to build video into a product</h2>
        <div className="features-table-container">
          <table className="features-table">
            <thead>
              <tr>
                <th>Adding video/ Going live</th>
                <th>Processing</th>
                <th>Transform & Streaming video</th>
                <th>In-video AI</th>
                <th>Video Data</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div className="feature-category">
                    <strong>Adding Video:</strong>
                    <ul>
                      <li>Push and direct uploads</li>
                      <li>Pull-based and CMS inputs</li>
                      <li>Multiple input formats</li>
                      <li>Accelerated uploads</li>
                      <li>Batch migration</li>
                      <li>Access policies</li>
                      <li>Webhooks</li>
                    </ul>
                  </div>
                  <div className="feature-category">
                    <strong>Going live:</strong>
                    <ul>
                      <li>Live streaming</li>
                      <li>RTMPS input</li>
                      <li>SRT input</li>
                      <li>Instant live encoding</li>
                      <li>Live to VOD recording</li>
                    </ul>
                  </div>
                </td>
                <td>
                  <ul>
                    <li>Real-time encoding</li>
                    <li>Context-aware encoding</li>
                    <li>H.264 and AV1 support</li>
                    <li>Adaptive bitrate ladders</li>
                    <li>Audio normalization</li>
                    <li>Replace audio</li>
                    <li>Audio overlay</li>
                  </ul>
                </td>
                <td>
                  <div className="feature-category">
                    <strong>Video clipping:</strong>
                    <ul>
                      <li>Video clipping</li>
                      <li>Instant live clipping</li>
                      <li>Video stitching</li>
                      <li>Watermarking</li>
                      <li>Intro and outro sections</li>
                      <li>Remove unwanted visuals</li>
                      <li>Time shifting</li>
                      <li>Sprite sheets</li>
                      <li>GIF thumbnails</li>
                    </ul>
                  </div>
                  <div className="feature-category">
                    <strong>Streaming:</strong>
                    <ul>
                      <li>Signed URLs</li>
                      <li>DRM</li>
                      <li>Adaptive bitrate streaming</li>
                      <li>Multi-CDN delivery</li>
                      <li>Low-latency live streaming</li>
                      <li>Live simulcasting</li>
                      <li>Concurrent streams</li>
                      <li>Offline viewing</li>
                    </ul>
                  </div>
                </td>
                <td>
                  <ul>
                    <li>Video summaries</li>
                    <li>Video chapters</li>
                    <li>AI clipping</li>
                    <li>NSFW and profanity filtering</li>
                    <li>Named entity recognition</li>
                  </ul>
                </td>
                <td>
                  <ul>
                    <li>View sessions</li>
                    <li>Video QoE analytics insights</li>
                    <li>Audience metrics</li>
                    <li>Play Metrics</li>
                    <li>Failure metrics</li>
                    <li>Stream metadata</li>
                    <li>Custom metadata</li>
                    <li>Exception alerts</li>
                    <li>Custom alerts</li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="divider-line"></div>

      <div className="section-6">
        <div className="section-6-content">
          <div className="section-6-text">
            <h2 className="section-6-title">Usage-based pricing</h2>
            <p className="section-6-intro">Video usage varies. Some days are low volume. Some aren't.</p>
            <p className="section-6-description">
              So pricing here is based on usage how much video is processed, streamed, and stored rather than fixed plans or long-term commitments. Costs move with usage, not ahead of it.
            </p>
            <p className="section-6-description">
              That makes it easier to start small, scale naturally, and understand what you're paying for as video becomes part of the system.
            </p>
            <p className="section-6-description">
              Pricing is transparent, and you can use the platform before deciding how far you want to go.
            </p>
          </div>
          <div className="section-6-illustration">
            <img src={pricingImage} alt="Pricing" className="pricing-image" />
          </div>
        </div>
      </div>

      <div className="divider-line"></div>

      <div className="section-7">
        <div className="section-7-content">
          <div className="section-7-text">
            <h2 className="section-7-title">TL;DR</h2>
            <p className="section-7-description">
              Whether you're looking to run video in your product or build something bigger around it this is being built for the long haul.
            </p>
            <p className="section-7-description">
              If you want to know more, go to FastPix.io. This isn't a short-term experiment or something we plan to flip in a few years.
            </p>
            <p className="section-7-description">
              We're building the video platform we've always wanted to use ourselves and the kind of company we'd want to build video from...
            </p>
          </div>
          <div className="section-7-illustration">
            <img src={tldrImage} alt="TL;DR" className="tldr-image" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;

